import { v4 as uuid } from 'uuid';

import { getFormatObjects, getStyleObjects, updateAllQuillObjects } from '@23degrees/shared-code/content/content-utilities';
import type { Content, User } from '@23degrees/shared-code/models';
import { cloneObject, createHashSlug, uniqueFilter } from '@23degrees/shared-code/utilities';
import type { Condition } from '@23degrees/shared/models/conditions';
import { CLONE_FLAG, WIZARD_FLAG } from '@config';

export const isOwner = (content: Content, user: string | User): boolean => {
  if (!content || !user) return false;
  const userId = typeof user === 'string' ? user : user._id;
  if (content.userId === userId) return true;
  return false;
};

export const hasShareRights = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  if (content.userId === user._id) return true;
  return content.shares?.some((share) => share.canShare && share.slug === user.slug);
};

export const hasWriteRights = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  if (content.deleted_at) return false;
  if (content.userId === user._id) return true;
  return content.shares?.some((share) => share.canWrite && share.slug === user.slug);
};

export const otherHasWriteRights = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  if (content.deleted_at) return false;
  if (content.userId === user._id) {
    // console.log('OTHER SHAREE WRITE', content.shares?.some((share) => share.canWrite));
    return content.shares?.some((share) => share.canWrite);
  }
  return content.shares?.some((share) => share.canWrite && share.slug === user.slug);
};

export const isWriteShared = (content: Content, user: User): boolean => {
  return content.shares?.some((share) => share.canWrite && share.slug === user.slug);
};

export const isShared = (content: Content, user: User): boolean => {
  return content.shares?.some((share) => share.slug === user.slug);
};

export const hasReadRights = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  if (content.userId === user._id) return true;
  return content.shares?.some((share) => share.slug === user.slug);
};

export const hasDataSetRights = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  if (content.type === 'dataset') {
    return hasReadRights(content, user);
  }
  return content.vDataSet?.shares?.some((share) => share.slug === user.slug);
};

export const hasSaveAsRight = (content: Content, user: User): boolean => {
  if (!content || !user) return false;
  //is edit blocked
  if (content.typeSpecific?.permissions) {
    const edit = content.typeSpecific.permissions.edit;
    if (edit.state === 'deny' && edit.owner !== user._id) {
      return false;
    }
  }
  return true;
};

// Resolve shortcut content
export const realContent = (content: Content): Content => {
  if (content.type === 'shortcut') {
    return content.typeSpecific.shortcutContent;
  }
  return content;
};

// Check if content is a statistic without data referenced to it
// TODO: Make some real checks
export const isDataLessViz = (content: Content): boolean => {
  //return true;
  if (!content) return true;
  if (!content.typeSpecific) return true;
  if (!content.typeSpecific.dataId) return true;
  if (!content.typeSpecific.data) return true;

  return false;
};

export const isContentInjected = (content: Content): boolean => {
  if (content) {
    if (content.injected) return true;
    if (!content.folderSlug && content.typeSpecific && !content.typeSpecific.isChild) return true;
  }
  return false;
};

export const cleanSlug = (slug: string) => {
  if (!slug) return;

  if (slug.includes(WIZARD_FLAG)) {
    slug = slug.substring(0, slug.indexOf(WIZARD_FLAG));
  }

  if (slug.includes(CLONE_FLAG)) {
    slug = slug.substring(0, slug.indexOf(CLONE_FLAG));
  }

  return slug;
};

export const filterCondition = (condition: Condition, data: any[], limit: number = 10): any[] => {
  const timeStart = Date.now();

  const { fieldName } = condition.field;

  const filterValues: any[] = [];
  const uniqueData = uniqueFilter(data, (row) => row[fieldName]);

  if (condition.function === 'equals') {
    filterValues.push(condition.value);
  } else {
    if (condition.function === 'min') {
      uniqueData.sort((a, b) => a[fieldName] - b[fieldName]);
    } else if (condition.function === 'max') {
      uniqueData.sort((a, b) => b[fieldName] - a[fieldName]);
    } else if (condition.function === 'earliest') {
      uniqueData.sort((a, b) => a['~meta'].moment.diff(b['~meta'].moment));
    } else if (condition.function === 'latest') {
      uniqueData.sort((a, b) => b['~meta'].moment.diff(a['~meta'].moment));
    }

    for (let idx = 0; idx < Math.min(limit, uniqueData.length); idx++) {
      filterValues.push(uniqueData[idx][fieldName]);
    }
  }

  const retData = data.filter((row) => filterValues.includes(row[fieldName]));
  const timeEnd = Date.now();

  console.log('[filterCondition] took', timeEnd - timeStart, 'ms');

  return retData;
};

export const resolveCondition = (condition: Condition, haystack: any[], limit: number = 10): any[] => {
  const fieldName = condition.field?.fieldName;
  const fn = condition.function;

  let tmp: any = {};
  let result: any[] = [];

  const arr = cloneObject(haystack);
  let i = 0;
  let idx = -1;
  let items = [];
  const max = haystack.length < limit ? haystack.length : limit;

  if (fieldName && fn && haystack.length) {
    const curTime = Date.now();
    switch (fn) {
      case 'equals':
        result = [haystack.filter((row) => row[fieldName] === condition.value)];
        break;
      case 'min':
        while (arr.length && i < max) {
          // 1. Get the min value of arr
          tmp = arr.reduce((prev, curr) => (prev[fieldName] < curr[fieldName] ? prev : curr));
          // 2. Get more elements from arr that are equal to the min value
          items = arr.filter((row) => row[fieldName] === tmp[fieldName]);
          // 3. Push these elements to the result array
          result.push(items);
          // 4. Remove these elements from the arr
          items.map((_item) => {
            idx = arr.findIndex((item) => item[fieldName] === _item[fieldName]);
            arr.splice(idx, 1)[0];
          });
          i++;
        }
        break;
      case 'max':
        while (arr.length && i < max) {
          tmp = arr.reduce((prev, curr) => (prev[fieldName] > curr[fieldName] ? prev : curr));
          items = arr.filter((row) => row[fieldName] === tmp[fieldName]);
          result.push(items);
          items.map((_item) => {
            idx = arr.findIndex((item) => item[fieldName] === _item[fieldName]);
            arr.splice(idx, 1)[0];
          });
          i++;
        }
        break;
      case 'earliest':
        while (arr.length && i < max) {
          tmp = arr.reduce((prev, curr) => (prev['~meta'].moment < curr['~meta'].moment ? prev : curr));
          items = arr.filter((row) => row[fieldName] === tmp[fieldName]);
          result.push(items);
          items.map((_item) => {
            idx = arr.findIndex((item) => item[fieldName] === _item[fieldName]);
            arr.splice(idx, 1)[0];
          });
          i++;
        }
        break;
      case 'latest':
        while (arr.length && i < max) {
          tmp = arr.reduce((prev, curr) => (prev['~meta'].moment > curr['~meta'].moment ? prev : curr));
          items = arr.filter((row) => row[fieldName] === tmp[fieldName]);
          result.push(items);
          items.map((_item) => {
            idx = arr.findIndex((item) => item[fieldName] === _item[fieldName]);
            arr.splice(idx, 1)[0];
          });
          i++;
        }
        break;
      // ...
      default:
        break;
    }
    console.log('[resolveCondition] took', Date.now() - curTime, 'ms');
    return result;
  }
  return [];
};

export const hostileContentTakeover = (content: Content, user: User): boolean => {
  // parent
  content.parentId = undefined;
  content.parentSlug = undefined;

  // inline
  content.injected = true;
  content.typeSpecific.inlineData = true;

  // folder
  content.folderSlug = undefined;

  // user
  content.userId = user._id;
  content.userInfo.name = user.name;
  content.userInfo.slug = user.slug;

  if (content.vUserInfo) {
    content.vUserInfo._id = user._id;
    content.vUserInfo.name = user.name;
    content.vUserInfo.slug = user.slug;
    content.vUserInfo.verifiedDataProvider = false; // TODO?
    content.vUserInfo.userPreferences.defaultContentLogo = user.userPreferences.defaultContentLogo;
  }

  // permissions
  content.typeSpecific.permissions.edit.owner = user._id;
  content.typeSpecific.permissions.embed.owner = user._id;
  content.typeSpecific.permissions.exportPNG.owner = user._id;
  content.typeSpecific.permissions.exportSVG.owner = user._id;
  content.typeSpecific.permissions.exportCSV.owner = user._id;
  content.typeSpecific.permissions.exportXLS.owner = user._id;
  content.typeSpecific.permissions.exportJSON.owner = user._id;
  content.typeSpecific.permissions.contentSave.owner = user._id;
  content.typeSpecific.permissions.templateSave.owner = user._id;

  // share info
  content.shares = [];
  content.shareCoding = [];

  // fields
  if ('auto23degreesindex' in content.typeSpecific.fields) {
    delete content.typeSpecific.fields['auto23degreesindex'];
  }

  if ('auto23degreesseries' in content.typeSpecific.fields) {
    delete content.typeSpecific.fields['auto23degreesseries'];
  }

  if (content.typeSpecific.style?.colors2) {
    content.typeSpecific.style.colors2.reevaluateOnChange = true;
  }

  for (const field of Object.values(content.typeSpecific.fields)) {
    field.format.number.optimalRounding = true;
  }

  const formatObjects = getFormatObjects(content);
  for (const format of formatObjects) {
    format.number.optimalRounding = true;
  }

  updateAllQuillObjects(content, (quillObj) => {
    const formatObjects = getFormatObjects(quillObj);
    for (const format of formatObjects) {
      format.number.optimalRounding = true;
    }

    const styleObjects = getStyleObjects(quillObj);
    for (const style of styleObjects) {
      // @ts-ignore
      delete style.font; // quill has font instead of fontFamily
    }

    return quillObj;
  });

  // slug
  content.slug = createHashSlug('clone');

  content.created_at = new Date();
  content.versioning.source_id = uuid();

  // console.log('[hostileContentTakeover]', cloneObject(content));

  return true;
};

export const hostileDataSetTakeover = (content: Content, dataSet: Content) => {
  content.dataSetSlug = dataSet.slug;
  content.dataSetOwnerId = dataSet.userId;

  content.vDataSet = dataSet.vDataSet;
  content.vDataSetUserInfo = dataSet.vDataSetUserInfo;

  // update data reference
  content.typeSpecific.dataId = dataSet.typeSpecific.dataId;

  // update versioning reference
  content.versioning.source_id = dataSet.versioning.source_id;

  // console.log('[hostileDataSetTakeover]', cloneObject(content));

  return content;
};
